<script setup lang="ts">
import { computed } from 'vue';
import { UiDialog, UiText } from '@shopexperts/shared';
import type { AdminExpertClaim } from '@shopexperts/legacy-api';

const props = defineProps<{
  claim: AdminExpertClaim | null;
  isLoading?: boolean;
}>();

const model = defineModel<boolean>({ default: false });

const emit = defineEmits<{
  confirm: [];
}>();

const isApproval = computed(() => props.claim?.decision === 'approved');
</script>

<template>
  <UiDialog
    v-model="model"
    :title="isApproval ? 'Cancel approval' : 'Cancel rejection'"
    :text="claim ? `Reopen ${claim.claimant_name || claim.email}'s claim of ${claim.expert.name || claim.expert.slug}?` : ''"
    primary-action-label="Cancel decision"
    primary-action-variant="danger"
    secondary-action-label="Keep decision"
    :loading="isLoading"
    @primary-action="emit('confirm')"
    @secondary-action="model = false"
  >
    <div v-if="isApproval">
      <UiText as="p" variant="p-small" class="text-gray-600 mb-1.5" text="Cancelling this approval will:" />
      <ul class="list-disc pl-5 space-y-1">
        <li>
          <UiText
            as="span"
            variant="p-small"
            class="text-gray-600"
            text="Remove the claimant's ownership of this expert's organization"
          />
        </li>
        <li>
          <UiText
            as="span"
            variant="p-small"
            class="text-gray-600"
            text="Delete the account that was created for them if it was never activated — an activated account is kept"
          />
        </li>
        <li>
          <UiText
            as="span"
            variant="p-small"
            class="text-gray-600"
            text="Restore any claims that were auto-rejected by this approval"
          />
        </li>
        <li>
          <UiText
            as="span"
            variant="p-small"
            class="text-gray-600"
            text="Return this claim to undecided"
          />
        </li>
      </ul>
    </div>
    <UiText
      v-else
      as="p"
      variant="p-small"
      class="text-gray-600"
      text="This reopens the claim for review. No other changes are made."
    />
  </UiDialog>
</template>
